import React, { useEffect, useState, useCallback, useContext, useMemo, useRef } from "react";
import api, { BASE_URL } from "../../Utils/api";
import { Alert, Badge, Button, Spinner } from "react-bootstrap";
import { FiMail } from "react-icons/fi";
import AgendamentoDocumentoModal from "./AgendamentoDocumentoModal";
import DocumentosModal from "../Documentos/DocumentosModal";
import GridPadrao from "../GridPadrao/GridPadrao";
import PageHeader from "../PageHeader/PageHeader";
import { formatarDataHora } from "../../Utils/calcularProximaDataExecucao";
import { GlobalContext } from "../../Utils/GlobalContext";
import "bootstrap/dist/css/bootstrap.min.css";


function Agendamentos() {
  const [agendamentos, setAgendamentos] = useState([]);
  const [totalAgendamentos, setTotalAgendamentos] = useState(0);
  const [tiposAgendamento, setTiposAgendamento] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [gerarResult, setGerarResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedDocumento, setSelectedDocumento] = useState(null);
  const [showDocumentoModal, setShowDocumentoModal] = useState(false);
  const [documentoParaModal, setDocumentoParaModal] = useState(null);
  const [execResultado, setExecResultado] = useState(null);
  const lastFetchParamsRef = useRef(null);
  const hasAgendamentosRef = useRef(false);

  const defaultGridFilters = useMemo(
    () => ({ filters: {}, dateFilters: {} }),
    []
  );

  const token = localStorage.getItem("authToken");
  const { globalData } = useContext(GlobalContext);
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");
  const userId = globalData?.user_id || localStorage.getItem("user_id");
  const numeroLinhasGrid = Number(globalData?.numero_linhas_grid) || 10;

  // ── fetchers ────────────────────────────────────────────────────────────────

  const fetchAgendamentos = useCallback(async (params) => {
    if (!entidadeID) {
      hasAgendamentosRef.current = false;
      setLoading(false);
      return;
    }

    const fetchParams = params || lastFetchParamsRef.current || {
      pagina: 1,
      limite: numeroLinhasGrid,
      ordenar: "data_proxima_execucao",
      direcao: "DESC",
      filters: {},
    };
    lastFetchParamsRef.current = fetchParams;
    if (!hasAgendamentosRef.current) {
      setLoading(true);
    }

    try {
      const response = await api.get(`${BASE_URL}/api/agendamentos/ListarTodosAgendamentos/${entidadeID}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          userId: userId ? Number(userId) || userId : undefined,
          pagina: fetchParams.pagina,
          limite: fetchParams.limite,
          ordenar: fetchParams.ordenar,
          direcao: fetchParams.direcao,
          situacao: fetchParams.filters?.situacao || undefined,
        },
      });
      const lista = Array.isArray(response.data?.data) ? response.data.data : [];
      setAgendamentos(lista);
      hasAgendamentosRef.current = lista.length > 0;
      setTotalAgendamentos(response.data?.paginacao?.total ?? lista.length);
    } catch (error) {
      console.error("Erro ao buscar agendamentos:", error);
    } finally {
      setLoading(false);
    }
  }, [entidadeID, token, userId, numeroLinhasGrid]);

  const handleServerParamsChange = useCallback((params) => {
    fetchAgendamentos(params);
  }, [fetchAgendamentos]);

  useEffect(() => {
    hasAgendamentosRef.current = false;
    lastFetchParamsRef.current = null;
  }, [entidadeID, userId]);

  const fetchTiposAgendamento = useCallback(() => {
    if (!entidadeID) return;
    api
      .get(`${BASE_URL}/api/tiposAgendamentos/ListarTiposAgendamento/${entidadeID}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => setTiposAgendamento(Array.isArray(response.data?.data) ? response.data.data : []))
      .catch((error) => console.error("Erro ao buscar tipos de agendamento:", error));
  }, [entidadeID, token]);

  useEffect(() => {
    fetchTiposAgendamento();
  }, [fetchTiposAgendamento]);

  // ── handlers ────────────────────────────────────────────────────────────────

  const handleOpenDocumento = useCallback((idDocumento) => {
    setDocumentoParaModal({ id: idDocumento });
    setShowDocumentoModal(true);
  }, []);

  const handleExecutarAgendamento = useCallback(async (row) => {
    const id = row.id;
    setExecResultado(null);
    try {
      const { data } = await api.post(
        `${BASE_URL}/api/agendamentos/ExecutarAgendamento/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExecResultado({ type: data.sucesso !== false ? "success" : "warning", message: data.mensagem || data.message });
      fetchAgendamentos(lastFetchParamsRef.current);
    } catch (err) {
      setExecResultado({ type: "danger", message: err.response?.data?.message || "Erro ao executar agendamento." });
    }
  }, [token, fetchAgendamentos]);

  const handleGerarAgendamentos = async () => {
    setGerando(true);
    setGerarResult(null);
    try {
      const { data } = await api.post(
        `${BASE_URL}/api/gerarAgendamentos`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGerarResult({ type: "success", message: data.message || "Agendamentos gerados com sucesso." });
      fetchAgendamentos(lastFetchParamsRef.current);
    } catch (error) {
      setGerarResult({
        type: "danger",
        message: error.response?.data?.message || "Erro ao gerar agendamentos.",
      });
    } finally {
      setGerando(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDocumento(null);
    fetchAgendamentos(lastFetchParamsRef.current);
  };

  // ── columns (depois de todos os handlers) ───────────────────────────────────

  const columns = [
    {
      Header: "ID",
      accessor: "id",
      Cell: ({ row }) =>
        row.id_documento ? (
          <span
            style={{ color: "var(--bs-link-color, #0d6efd)", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleOpenDocumento(row.id_documento)}
          >
            {row.id}
          </span>
        ) : (
          row.id || "—"
        ),
    },
    { Header: "Tipo Documento",    accessor: "tipo_documento",     Cell: ({ row }) => row.tipo_documento     || "—" },
    { Header: "Empresa",           accessor: "empresa",            Cell: ({ row }) => row.empresa            || "—" },
    { Header: "Fornecedor",        accessor: "fornecedor",         Cell: ({ row }) => row.fornecedor         || "—" },
    { Header: "Funcionário",       accessor: "funcionario",        Cell: ({ row }) => row.funcionario        || "—" },
    { Header: "Usuário Notificado",accessor: "usuario_notificado", Cell: ({ row }) => row.usuario_notificado || "—" },
    { Header: "E-mail",            accessor: "email",              Cell: ({ row }) => row.email              || "—" },
    {
      Header: "Data Final",
      accessor: "data_final",
      type: "date",
      Cell: ({ row }) => row.data_final ? formatarDataHora(row.data_final) : "—",
    },
    {
      Header: "Data Próx. Execução",
      accessor: "data_proxima_execucao",
      type: "date",
      Cell: ({ row }) => row.data_proxima_execucao ? formatarDataHora(row.data_proxima_execucao) : "—",
    },
    {
      Header: "Data Execução",
      accessor: "data_execucao",
      type: "date",
      Cell: ({ row }) => row.data_execucao ? formatarDataHora(row.data_execucao) : "—",
    },
    {
      Header: "Situação",
      accessor: "situacao",
      Cell: ({ row }) => {
        const s = row.situacao;
        if (s === "A") return <Badge bg="warning" text="dark">Agendado</Badge>;
        if (s === "E") return <Badge bg="success">Executado</Badge>;
        if (s === "C") return <Badge bg="danger">Cancelado</Badge>;
        return s || "—";
      },
    },
  ];

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader titulo="Agendamentos" path="/Agendamentos" />
      {(gerarResult || execResultado) && (
        <div className="d-flex gap-3 mb-3 flex-wrap">
          {gerarResult && (
            <Alert variant={gerarResult.type} dismissible onClose={() => setGerarResult(null)} className="mb-0 py-2 px-3">
              {gerarResult.message}
            </Alert>
          )}
          {execResultado && (
            <Alert variant={execResultado.type} dismissible onClose={() => setExecResultado(null)} className="mb-0 py-2 px-3">
              {execResultado.message}
            </Alert>
          )}
        </div>
      )}
      <GridPadrao
        columns={columns}
        data={agendamentos}
        rota="agendamentos"
        defaultSortColumn="data_proxima_execucao"
        defaultSortOrder="desc"
        defaultFilters={defaultGridFilters}
        situacaoFilterOptions={[
          { value: "A", label: "Agendado" },
          { value: "E", label: "Executado" },
          { value: "C", label: "Cancelado" },
        ]}
        onAcao={handleExecutarAgendamento}
        AcaoIcone={FiMail}
        acaoTitulo="Forçar envio de notificação"
        acaoVariant="outline-primary"
        serverSide
        totalItems={totalAgendamentos}
        loading={loading}
        onServerParamsChange={handleServerParamsChange}
        acoesBotoes={
          <Button variant="primary" size="sm" onClick={handleGerarAgendamentos} disabled={gerando}>
            {gerando ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Gerando...
              </>
            ) : (
              "Gerar Agendamentos"
            )}
          </Button>
        }
      />
      <AgendamentoDocumentoModal
        show={showModal}
        handleClose={handleCloseModal}
        documento={selectedDocumento}
        tiposAgendamento={tiposAgendamento}
        onAgendamentoAdded={fetchAgendamentos}
      />
      {showDocumentoModal && (
        <DocumentosModal
          show={showDocumentoModal}
          handleClose={() => { setShowDocumentoModal(false); setDocumentoParaModal(null); }}
          documento={documentoParaModal}
          fetchDocumentos={fetchAgendamentos}
        />
      )}
    </div>
  );
}

export default Agendamentos;
